const noticeEventByMethod = (noticeId: string): Record<string, string> => ({
  GET: `Abriu o edital #${noticeId}`,
  PATCH: `Atualizou o edital #${noticeId}`,
  PUT: `Atualizou o edital #${noticeId}`,
  DELETE: `Removeu o edital #${noticeId}`,
});

// A nested key is tried before its first segment.
const noticeSubresourceEvents: Record<string, Record<string, string>> = {
  announcements: {
    GET: 'Visualizou os comunicados do edital',
    POST: 'Publicou um comunicado no edital',
  },
  attachments: {
    GET: 'Visualizou os anexos do edital',
    POST: 'Enviou um anexo ao edital',
  },
  contract: {
    GET: 'Visualizou o contrato do edital',
    POST: 'Enviou o contrato do edital',
  },
  'criteria-groups': {
    GET: 'Visualizou os grupos de critérios do edital',
    POST: 'Criou um grupo de critérios',
    PATCH: 'Atualizou um grupo de critérios',
    DELETE: 'Removeu um grupo de critérios',
  },
  'criteria-groups/{id}/add': {
    PATCH: 'Adicionou critérios ou serviços a um grupo de critérios',
  },
  'criteria-groups/{id}/remove': {
    PATCH: 'Removeu critérios de um grupo de critérios',
  },
  'licensed-companies': {
    GET: 'Visualizou as empresas do edital',
    POST: 'Incluiu uma empresa no edital',
  },
  'notice-coverages': {
    GET: 'Visualizou a cobertura do edital',
    POST: 'Definiu a cobertura do edital',
  },
  'notice-coverages/single': {
    POST: 'Adicionou uma cidade à cobertura do edital',
  },
  'pole-group-limits': {
    GET: 'Visualizou os limites por grupo do polo',
    PATCH: 'Atualizou os limites por grupo do polo',
  },
  poles: { GET: 'Visualizou os limites por polo do edital' },
  'typology-groups': { GET: 'Visualizou os grupos de tipologia do edital' },
  waitlist: {
    GET: 'Visualizou a lista de espera do edital',
    POST: 'Ofereceu uma vaga da lista de espera',
  },
};

const workOrderEventByMethod = (
  workOrderId: string
): Record<string, string> => ({
  GET: `Abriu a OS #${workOrderId}`,
  PATCH: `Atualizou a OS #${workOrderId}`,
  PUT: `Atualizou a OS #${workOrderId}`,
  DELETE: `Removeu a OS #${workOrderId}`,
});

const workOrderSubresourceEvents: Record<string, Record<string, string>> = {
  associate: { POST: 'Associou outras OS a esta' },
  associated: { GET: 'Visualizou as OS associadas' },
  attachments: {
    GET: 'Visualizou os anexos da OS',
    POST: 'Enviou um anexo à OS',
  },
  'avm-info': {
    GET: 'Visualizou os dados AVM da OS',
    PUT: 'Atualizou os fatores AVM da OS',
  },
  cancel: { PUT: 'Cancelou a OS' },
  'facade-image': { PUT: 'Enviou a imagem da fachada' },
  'generate-report-pdf': { PUT: 'Gerou o PDF do laudo' },
  'google-maps-images': { PUT: 'Enviou as imagens do mapa' },
  'hive-eligibility': { GET: 'Verificou a elegibilidade da OS para o Hive' },
  'hive-messages': {
    GET: 'Visualizou as mensagens do Hive da OS',
    POST: 'Enviou uma mensagem no Hive',
  },
  'hive-messages/read': { PATCH: 'Marcou as mensagens do Hive como lidas' },
  'hive-messages/unread-count': {
    GET: 'Verificou as mensagens não lidas do Hive',
  },
  'hive-requests': { GET: 'Visualizou os pedidos do Hive da OS' },
  'hive-requests/work-status': {
    PATCH: 'Atualizou o status do pedido no Hive',
  },
  'invoice-receipt': { PUT: 'Enviou a nota fiscal e o recibo' },
  'manual-calculation-report': {
    GET: 'Visualizou o cálculo manual',
    PATCH: 'Atualizou o cálculo manual',
  },
  'manual-calculation-report/completion': {
    GET: 'Verificou o preenchimento do cálculo manual',
  },
  proof_of_payment: { PUT: 'Enviou o comprovante de pagamento' },
  'property-characteristics/completion': {
    GET: 'Verificou o preenchimento das características do imóvel',
  },
  'report-graphs': {
    GET: 'Visualizou os gráficos do laudo',
    POST: 'Enviou os gráficos do laudo',
  },
  'report-pdf': { GET: 'Visualizou o PDF do laudo' },
  samples: {
    GET: 'Visualizou as amostras da OS',
    DELETE: 'Removeu as amostras da OS',
  },
  'samples-xlsx': { GET: 'Exportou as amostras da OS' },
  'scraper-ai-requests': { GET: 'Visualizou as buscas de amostras por IA' },
  'search-samples': {
    GET: 'Buscou amostras para a OS',
    POST: 'Buscou amostra automaticamente da OS #{id}',
  },
  status: { PUT: 'Alterou o status da OS' },
};

// The screen calls each sample an "elemento"; `{id}` is the element's id.
const sampleEventByMethod: Record<string, string> = {
  GET: 'Abriu o elemento #{id}',
  PUT: 'Editou o elemento #{id}',
  DELETE: 'Removeu o elemento #{id}',
};

const sampleSubresourceEvents: Record<string, Record<string, string>> = {
  approve: { PUT: 'Aprovou o elemento #{id}' },
  'original-element': {
    GET: 'Visualizou a versão original do elemento #{id}',
  },
  pictures: {
    GET: 'Visualizou as fotos do elemento #{id}',
    POST: 'Enviou fotos do elemento #{id}',
  },
  'search-sample-pictures': { GET: 'Buscou fotos do elemento #{id}' },
};

const noticeCompanySubresourceEvents: Record<string, Record<string, string>> = {
  acceptances: { POST: 'Aceitou um documento do edital no credenciamento' },
  'approve-formalization': {
    PATCH: 'Aprovou a formalização do contrato do credenciamento',
  },
  'change-requests': {
    GET: 'Visualizou os pedidos de alteração de equipe do credenciamento',
  },
  'criteria-documents': {
    GET: 'Visualizou os documentos de critérios do credenciamento',
  },
  documents: {
    GET: 'Visualizou os documentos do credenciamento',
    POST: 'Solicitou um documento adicional no credenciamento',
  },
  'documents/{id}/approve': {
    PATCH: 'Aprovou um documento do credenciamento',
  },
  'documents/{id}/approve-with-reservations': {
    PATCH: 'Aprovou com ressalvas um documento do credenciamento',
  },
  'documents/{id}/cancel': {
    PATCH: 'Cancelou um documento adicional do credenciamento',
  },
  'documents/{id}/reject': {
    PATCH: 'Rejeitou um documento do credenciamento',
  },
  'documents/{id}/request-replacement': {
    POST: 'Solicitou a substituição de um documento do credenciamento',
  },
  'documents/{id}/status-history': {
    GET: 'Visualizou o histórico de um documento do credenciamento',
  },
  'reject-formalization': {
    PATCH: 'Recusou a formalização do contrato do credenciamento',
  },
  team: { GET: 'Visualizou a equipe do credenciamento' },
  terms: { GET: 'Visualizou os termos do credenciamento' },
};

const workOrderListEvents: Record<string, string> = {
  archived: 'Visualizou as OS arquivadas',
  'check-registry': 'Verificou se a matrícula já está em outra OS',
  filters: 'Carregou as opções de filtro de OS',
  hive: 'Visualizou as OS do Hive',
  'search-archived-registry': 'Buscou OS arquivadas com a mesma matrícula',
  top: 'Carregou o pipeline de OS',
};

export {
  noticeCompanySubresourceEvents,
  noticeEventByMethod,
  noticeSubresourceEvents,
  sampleEventByMethod,
  sampleSubresourceEvents,
  workOrderEventByMethod,
  workOrderListEvents,
  workOrderSubresourceEvents,
};
